<script setup>
import CustomButton from "@/components/Button/Button.vue";
import FileUpload from "@/components/FileUpload/FileUpload.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useUserStore} from "@/stores/userStore";
import {storeToRefs} from "pinia";
import {vMaska} from "maska/vue";
import {getAllCountries} from "@/services/country.service";
import {formatSize, processFileUpload, validateFile} from "@/composables/useFileUpload";
import {useDocumentUpload} from "@/composables/useDocumentUpload";
import {useValidationRules} from "@/composables/useValidationRules";
import * as DateUtils from "@/helpers/dateUtils";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {VueSpinner} from "vue3-spinners";

// ... existing code ...

const {t, locale} = useI18n();
const userStore = useUserStore();
const {user, sessionRestored} = storeToRefs(userStore);

// Optional passport rules: only validate if user provided a value
const {passportExpiryDateRule, passportNumberRule} = useValidationRules();
const optionalPassportNumberRule = (v) => {
  if (v == null || String(v).trim() === '') return true;
  return passportNumberRule(v);
};
const optionalpassportExpiryDateRule = (v) => {
  if (v == null || String(v).trim() === '') return true;
  return passportExpiryDateRule(v);
};

const formUser = ref({});
const saveSuccess = ref(false);
const saveError = ref(false);
const fileUploadError = ref('');
const isDragging = ref(false);
const formRef = ref(null);
const isSaving = ref(false)


const dateInputConfig = computed(() =>
    DateUtils.getDateMaskAndPlaceholder(locale.value)
);

onMounted(() => {
  showInitials.value = false;
})

watch(
    () => sessionRestored.value,
    async (restored) => {
      if (restored) {
        try {
          // First ensure user profile is loaded
          if (!user.value) {
            await userStore.fetchUserProfile();
          }

          // Populate form with user data
          if (user.value) {
            formUser.value = {...user.value};
          }

          // Load countries and documents
          const response = await getAllCountries();
          countries.value = response.data._embedded?.countries || [];

          // Load existing documents if they exist
          if (user.value?.passportDocuments?.length) {
            uploadedPassport.value = user.value.passportDocuments.map((doc) => ({
              uuid: doc.uuid,
              name: doc.fileName,
              size: formatSize(doc.size || 0),
              status: 'uploaded',
            }));
          }

          if (user.value?.licenseDocuments?.length) {
            uploadedLicense.value = user.value.licenseDocuments.map((doc) => ({
              uuid: doc.uuid,
              name: doc.fileName,
              size: formatSize(doc.size || 0),
              status: 'uploaded',
            }));
            toggleValue.value = true;
          }

          if (user.value?.vhfLicenseDocuments?.length) {
            uploadedVHFLicense.value = user.value.vhfLicenseDocuments.map((doc) => ({
              uuid: doc.uuid,
              name: doc.fileName,
              size: formatSize(doc.size || 0),
              status: 'uploaded',
            }));
            vhfToggleValue.value = true;
          }
        } catch (error) {
          console.error("Failed to load:", error);
        }
      }
    },
    {immediate: true}
);

const profileUploadInput = ref(null);
const licenseInputRef = ref(null);
const vhflicenseInputRef = ref(null);
const countries = ref([]);
const passportInputRef = ref(null);

const toggleValue = ref(false);
const vhfToggleValue = ref(false);

const {
  uploadedLicense,
  uploadedPassport,
  uploadedVHFLicense,
  licenseUploadError,
  passportUploadError,
  vhfLicenseUploadError,
  handleFileInput,
  handleFileDrop,
  removeUploadedFile
} = useDocumentUpload({
  uploadPassportDocument: userStore.uploadPassportDocument,
  uploadLicenseDocument: userStore.uploadLicenseDocument,
  uploadVHFLicenseDocument: userStore.uploadVHFLicenseDocument,
  deletePassportDocument: userStore.deletePassportDocument,
  deleteLicenseDocument: userStore.deleteLicenseDocument,
  deleteVHFLicenseDocument: userStore.deleteVHFLicenseDocument
});


function triggerProfileUpload() {
  profileUploadInput.value?.click();
}


function triggerLicenseUpload() {
  licenseInputRef.value?.click();
}

function triggerVhfLicenseUpload() {
  vhflicenseInputRef.value?.click();
}

function triggerPassportUpload() {
  passportInputRef.value?.click();
}

//Profile photo
async function handleProfilePhotoUpload(event) {
  fileUploadError.value = '';
  const file = event?.target?.files?.[0];
  if (!file) return;

  const isValid = validateFile(file, fileUploadError, ['image/jpeg', 'image/png', 'image/jpg'], 2);
  if (!isValid) return;

  try {
    await processFileUpload(file, userStore.uploadProfileImage, uploadedLicense, {
      onSuccess: async () => {
        await userStore.fetchUserProfile();
      },
      onError: () => {
        fileUploadError.value = "Image upload failed.";
      }
    });
  } catch (error) {
    console.error("Image upload failed:", error);
  }
}

async function handleSaveProfile() {
  isSaving.value = true;
  saveSuccess.value = false;
  saveError.value = false;

  const {valid} = await formRef.value.validate();

  if (!valid) {
    isSaving.value = false;
    return;
  }
  
  if(formUser.value.firstName || formUser.value.lastName){
    formUser.value.name = formUser.value.firstName + " " + formUser.value.lastName;
  }
    
  try {
    await userStore.updateUserProfile({...formUser.value});
    await userStore.fetchUserProfile();
    await userStore.getLicenseDocuments();
    await userStore.getPassportDocuments();
    await userStore.getVHFLicenseDocuments();
    saveSuccess.value = true;
  } catch (error) {
    console.error("Failed to save profile and refresh data:", error);
    saveError.value = true;
  } finally {
  isSaving.value = false;
  }
}

const showInitials = ref(false);

const initials = computed(() => {
  const first = user.value?.firstName ?? '';
  const last = user.value?.lastName ?? '';
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
});


</script>

<template>
  <div v-if="sessionRestored && user" class="w-3/5 flex flex-col gap-8 py-8 mainContainer">
    <div class="flex flex-col gap-2">
      <span class=" responsive-body-medium semiboldText">{{ t('profile.profilePicture') }}</span>
      <div class="flex align-center gap-6">
        <div v-if="user?.picture && showInitials === false" class="imageProfile">
          <img
              :src="user?.picture"
              :alt="user.name"
              @error="e => { e.target.style.display = 'none'; showInitials = true; }"
          />
        </div>
        <div v-if="showInitials" class="w-24 h-24 min-w-24 min-h-24 sm:w-44 sm:h-44">
          <div class="flex items-center justify-center w-full h-full overflow-hidden text-center rounded-full bg-bw-silver text-black font-bold responsive-title-2">
            {{ initials }}
          </div>
        </div>
   
        <div class="flex flex-col gap-2.5">
          <div 
              class="px-4 py-1 w-fit responsive-body-small font-semibold text-bw-black border-[1px] border-black rounded-full hover:bg-bw-black hover:text-bw-white duration-200 cursor-pointer"
              @click="triggerProfileUpload"
          >
            {{ t('profile.uploadPhoto') }}
          </div>
          <span class=" responsive-body-xsmall semiboldText text-wrap">{{ t('profile.fileRequirements') }}</span>
        </div>
      </div>
    </div>
    <div class="bg-notifications-completed px-5.5 py-2.5 rounded-8xl align-center font-bold responsive-body-small">
      {{ t('profile.infoNote') }}
    </div>

    <v-form ref="formRef">
      <div>
        <div class="grid grid-cols-2 gap-x-8 gap-y-6">
          <VTextField
              :label="`${t('profile.firstName')} *`"
              type="name"
              variant="underlined"
              v-model="formUser.firstName"
              :rules="[v => !!(v && String(v).trim()) || t('profile.firstNameRequired')]"
          />
          <VTextField
              :label="`${t('profile.lastName')} *`"
              type="surname"
              variant="underlined"
              v-model="formUser.lastName"
              :rules="[v => !!(v && String(v).trim()) || t('profile.lastNameRequired')]"
          />
        </div>
        <div class="flex gap-x-8 gap-y-6 rowClass">
          <VSelect
              :label="`${t('profile.nationality')} *`"
              variant="underlined"
              v-model="formUser.nationality"
              item-title="name"
              item-value="id"
              :items="countries"
              :return-object="false"
              class="w-full sm:w-1/2"
              :rules="[v => !!(v && String(v).trim()) || t('profile.nationalityRequired')]"
          />
          <VTextField
              class="w-full sm:w-1/2"
              :label="`${t('profile.birthdate')} *`"
              type="birthdate"
              variant="underlined"
              v-maska="dateInputConfig.mask"
              :placeholder="dateInputConfig.placeholder"
              v-model="formUser.birthDate"
              :rules="[v => !!(v && String(v).trim()) || t('profile.birthdateRequired')]"
          />
        </div>
        <div class="grid grid-cols-2 gap-x-8 gap-y-6">
          <VTextField
              :label="`${t('profile.email')} *`"
              type="email"
              variant="underlined"
              placeholder="you@example.com"
              :rules="[
                      v => !!(v && String(v).trim()) || t('profile.emailRequired'),
                       v => /.+@.+\..+/.test(v) || t('profile.emailInvalid')
                     ]"
              v-model="formUser.email"/>
          <VTextField
              :label="`${t('profile.phoneNumber')} *`"
              type="phone"
              variant="underlined"
              v-model="formUser.phone"
              :rules="[v => !!(v && String(v).trim()) || t('profile.phoneRequired')]"
          />
        </div>
        <div>
          <span class="responsive-body-medium">{{ t('profile.passportPhoto') }}</span>
          <FileUpload
              label="profile.uploadLicenseFiles"
              type="passport"
              :uploadFiles="uploadedPassport"
              :uploadError="passportUploadError"
              :isDragging="isDragging"
              :onUploadTrigger="triggerPassportUpload"
              :onDrop="handleFileDrop"
              :onFileChange="handleFileInput"
              :onRemove="removeUploadedFile"
          />
        </div>
        <div class="grid grid-cols-2 gap-x-8 gap-y-6">
          <VTextField
              :label="t('profile.passportNumber')"
              type="pass_number"
              variant="underlined"
              v-model="formUser.passportNumber"
              :rules="[optionalPassportNumberRule]"
          />
          <VTextField
              :label="t('profile.passportExpiryDate')"
              type="pass_date"
              variant="underlined"
              v-maska="dateInputConfig.mask"
              :placeholder="dateInputConfig.placeholder"
              v-model="formUser.passportExpiryDate"
              :rules="[optionalpassportExpiryDateRule]"
          />
        </div>
        <div>
          <div class="responsive-body-medium align-center flex gap-6 ml-5">
            <VSwitch
                v-model="toggleValue"
                inset
                base-color="grey"
                color="black"
                hide-details
                density="compact"
            />
            {{ t('profile.sailingLicense') }}
          </div>
          <FileUpload
              v-if="toggleValue"
              label="profile.uploadLicenseFiles"
              type="license"
              :uploadFiles="uploadedLicense"
              :uploadError="licenseUploadError"
              :isDragging="isDragging"
              :onUploadTrigger="triggerLicenseUpload"
              :onDrop="handleFileDrop"
              :onFileChange="handleFileInput"
              :onRemove="removeUploadedFile"
          />
        </div>
        <div>
          <div class="responsive-body-medium align-center flex gap-6 ml-5">
            <VSwitch
                v-model="vhfToggleValue"
                inset
                base-color="grey"
                color="black"
                hide-details
                density="compact"
            />
            {{ t('profile.vhfLicense') }}
          </div>
          <FileUpload
              v-if="vhfToggleValue"
              label="profile.uploadLicenseFiles"
              type="vhf"
              :uploadFiles="uploadedVHFLicense"
              :uploadError="vhfLicenseUploadError"
              :isDragging="isDragging"
              :onUploadTrigger="triggerVhfLicenseUpload"
              :onDrop="handleFileDrop"
              :onFileChange="handleFileInput"
              :onRemove="removeUploadedFile"
          />
        </div>
      </div>
    </v-form>
    <CustomButton
        type="black"
        class="w-fit py-3 px-7 mt-1"
        :outlined="false"
        :rounded="true"
        :disabled="isSaving"
        @click="handleSaveProfile">
      <span class="flex items-center gap-2">
        <VueSpinner v-if="isSaving" :size="16" color="#FFFFFF"/>
        <span>{{ t('profile.save') }}</span>
      </span>
    </CustomButton>

    <div v-if="fileUploadError" class="text-notifications-red responsive-body-xsmall mt-2">
      {{ fileUploadError }}
    </div>
    <div v-if="saveSuccess" class="text-notifications-confirmed responsive-body-xsmall">
      {{ t('profile.savedSuccessfully') }}
    </div>
    <div v-if="saveError" class="text-notifications-red responsive-body-xsmall">
      {{ t('profile.saveFailed') }}
    </div>
    <input
        type="file"
        accept="image/*"
        ref="profileUploadInput"
        class="hidden"
        @change="handleProfilePhotoUpload"
    />
  </div>

  <div v-else class="w-1/2 flex flex-col gap-8 py-12 mainContainer">
    <div class="w-fit px-5 py-2 rounded-22xl">
      <CustomSpinner/>
    </div>
  </div>
</template>

<style scoped>
:deep(.v-switch__track) {
  opacity: 1;
}

.semiboldText {
  color: #8C8B94;
}

.imageProfile {
  border: 1px solid #B7B6BE;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: scale-down;
}

.imageProfile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 50%;
  display: block;
}

/* For smaller screens */
@media (max-width: 768px) {
  .imageProfile {
    width: 100px;
    height: 100px;
    object-fit: scale-down;
  }
}

@media (max-width: 400px) {
  .imageProfile {
    width: 50%;
    height: 50%;
    object-fit: scale-down;
  }
}


.customBoxClass {
  border: 1px solid #EEEEF0;
  border-radius: 10px;
}

.customBorder {
  border-bottom: 1px solid #EEEEF0;
}

.progress {
  border-bottom: 1px solid #9BE7BA;
}

.drag-active {
  border: 2px dashed #1B84FF;
  background-color: #f0f8ff;
}

.rowClass {
  margin-bottom: -5px;
}

@media (max-width: 768px) {
  .mainContainer {
    width: 100%;
    padding: 37px 20px;
  }

  .rowClass {
    flex-direction: column;
    gap: 0px;
  }

  .rmMobile {
    display: none;
  }

  .photoPass {
    flex-direction: row-reverse;
  }

  .licensesClass {
    flex-direction: column;
    gap: 10px;
  }

  .licensesClass > div,
  .licensesClass > button {
    align-self: start;
  }
}

:deep(.modal-content) {
  min-width: 760px;
}


@media (max-width: 800px) {
  :deep(.modal-content) {
    min-width: auto;
    width: 100%;
  }
}
</style>
